// Client-side RIASEC scoring. Mirrors the server-side mapSelectionsToRIASEC
// in supabase/functions/generate-results/index.ts so the UI can show the
// user their interest profile live during the assessment AND on the results
// page, before the AI generation finishes.
//
// Why duplicate the mapping client-side? The server runs once per result
// generation. The client needs to update as the user clicks each option.
// Round-tripping for every answer would be slow and waste server resources.
//
// IMPORTANT: when the server mapping changes, this file must update too.
// Both files reference the same option IDs from src/data/{highschool,
// college,professional}.ts.

export type RiasecCode = 'realistic' | 'investigative' | 'artistic' | 'social' | 'enterprising' | 'conventional';

export interface RiasecLabel {
  code: RiasecCode;
  letter: 'R' | 'I' | 'A' | 'S' | 'E' | 'C';
  plain: string;        // Plain-language name for students
  oneLine: string;      // Single-line explanation
  blurb: string;        // What "high on this code" actually means in plain English
  fitsWith: string;     // Example fields a high-X person tends to thrive in
}

// Holland's RIASEC types in plain-language clothing. The plain names are
// chosen to be self-explanatory to a 14-year-old without dumbing down the
// underlying concept; counselors and parents will recognize the letter codes.
export const RIASEC_LABELS: Record<RiasecCode, RiasecLabel> = {
  realistic: {
    code: 'realistic',
    letter: 'R',
    plain: 'Builder',
    oneLine: 'Hands-on, practical, likes to fix or make real things.',
    blurb:
      'You probably learn fastest by doing, not just reading. You like clear, tangible results; the thing you built, the problem you fixed, the machine you got running.',
    fitsWith: 'Engineering, trades, healthcare, design-build, athletics, environmental work.',
  },
  investigative: {
    code: 'investigative',
    letter: 'I',
    plain: 'Analyst',
    oneLine: 'Curious, asks why, goes deep, loves a hard problem.',
    blurb:
      'You like to understand things from the ground up. You probably hate being told what to think and prefer to dig into the evidence. You see patterns other people miss.',
    fitsWith: 'Science, medicine, data, research, engineering, software, finance.',
  },
  artistic: {
    code: 'artistic',
    letter: 'A',
    plain: 'Creator',
    oneLine: 'Imagines, designs, expresses; needs freedom to do it your way.',
    blurb:
      'You bring things into the world that did not exist yet. You probably get bored quickly with rigid structure and need creative freedom to do your best work.',
    fitsWith: 'Design, media, writing, music, marketing, architecture, product.',
  },
  social: {
    code: 'social',
    letter: 'S',
    plain: 'Helper',
    oneLine: 'Connects with people, teaches, listens, makes others better.',
    blurb:
      'You read people well and they trust you. You tend to care about outcomes for others as much as your own, and you do your best work when there is a person on the other side of it.',
    fitsWith: 'Teaching, healthcare, counseling, social work, hospitality, HR.',
  },
  enterprising: {
    code: 'enterprising',
    letter: 'E',
    plain: 'Leader',
    oneLine: 'Takes initiative, persuades, builds momentum, comfortable with risk.',
    blurb:
      'You start things. You can convince other people to come along. You probably do not need permission to act, and you are comfortable being judged on results.',
    fitsWith: 'Business, sales, law, marketing, founding companies, politics, real estate.',
  },
  conventional: {
    code: 'conventional',
    letter: 'C',
    plain: 'Organizer',
    oneLine: 'Structures, systematizes, follows through, sweats the details.',
    blurb:
      'You make chaos make sense. You catch the details everyone else misses. People probably hand you the spreadsheet, the schedule, or the plan because you actually finish it.',
    fitsWith: 'Operations, accounting, project management, logistics, paralegal, data analytics.',
  },
};

// Interest mappings (each interest contributes to 1-2 RIASEC codes).
// MUST stay in sync with supabase/functions/generate-results/index.ts.
const INTEREST_MAP: Record<string, RiasecCode[]> = {
  'ai-tech': ['investigative', 'realistic'],
  'arts': ['artistic'],
  'business': ['enterprising'],
  'science': ['investigative'],
  'helping-people': ['social'],
  'health': ['social', 'investigative'],
  'media': ['artistic', 'enterprising'],
  'teaching': ['social'],
  'sports': ['realistic', 'social'],
  'music': ['artistic'],
  'environment': ['investigative', 'realistic'],
  'gaming': ['realistic', 'artistic'],
  'fashion': ['artistic', 'enterprising'],
  'cooking': ['realistic', 'artistic'],
  'writing': ['artistic', 'investigative'],
  'psychology': ['social', 'investigative'],
  'animals': ['realistic', 'investigative'],
  'law': ['enterprising', 'conventional'],
  'building': ['realistic'],
  'money': ['enterprising', 'conventional'],
  // College / Pro editions add these
  'social-impact': ['social', 'enterprising'],
  'education': ['social'],
  'finance': ['conventional', 'enterprising'],
  'data': ['investigative', 'conventional'],
  'investing': ['enterprising', 'conventional'],
  'coaching': ['social', 'enterprising'],
  'real-estate': ['enterprising'],
  'sustainability': ['investigative', 'realistic'],
  'product': ['enterprising', 'investigative'],
  'operations': ['conventional', 'enterprising'],
};

// Aspirations: future-field selections (HS + College editions). Each option
// maps to 1-2 RIASEC codes. Strong forward-looking signal.
const ASPIRATION_MAP: Record<string, RiasecCode[]> = {
  'ai-ml':              ['investigative', 'realistic'],
  'creator-economy':    ['artistic', 'enterprising'],
  'climate-tech':       ['investigative', 'realistic'],
  'healthcare-ai':      ['investigative', 'social'],
  'fintech':            ['investigative', 'conventional'],
  'edtech':             ['social', 'investigative'],
  'gaming-vr':          ['artistic', 'investigative'],
  'social-enterprise':  ['social', 'enterprising'],
  'space':              ['investigative', 'realistic'],
  'cybersecurity':      ['investigative', 'realistic'],
  'sports-science':     ['realistic', 'investigative'],
  'entertainment':      ['artistic', 'enterprising'],
  'architecture':       ['artistic', 'realistic'],
  'law-policy':         ['enterprising', 'conventional'],
  'biotech':            ['investigative', 'realistic'],
  // College / Pro additions
  'consulting':         ['enterprising', 'investigative'],
  'product-management': ['enterprising', 'investigative'],
  'real-estate-tech':   ['enterprising', 'conventional'],
};

// Dream-Wednesday vibes (single forced choice — high signal per pick).
const VIBE_MAP: Record<string, RiasecCode[]> = {
  'ceo-vibes':      ['enterprising'],
  'creator-vibes':  ['artistic'],
  'explorer-vibes': ['enterprising', 'artistic'],
  'helper-vibes':   ['social'],
  'builder-vibes':  ['realistic', 'enterprising'],
  'analyst-vibes':  ['investigative', 'conventional'],
};

// Values screen. Less direct than interests but adds Conventional + Social
// balance that the original mapping under-weighted.
const VALUE_MAP: Record<string, RiasecCode[]> = {
  'freedom':     ['enterprising'],
  'security':    ['conventional'],
  'creativity':  ['artistic'],
  'impact':      ['social'],
  'community':   ['social'],
  'growth':      ['investigative'],
  'justice':     ['social', 'enterprising'],
  'innovation':  ['enterprising', 'artistic'],
  'balance':     ['conventional'],
  'recognition': ['enterprising'],
  'adventure':   ['enterprising', 'realistic'],
  'family':      ['social'],
  'stability':   ['conventional'],
  'health':      ['social'],   // college edition adds this option
};

// Energy / workstyle: only the options with clear interest signal.
// Night-owl / morning-person / multitasker omitted (preferences, not interests).
const WORKSTYLE_MAP: Record<string, RiasecCode[]> = {
  'deep-focus':     ['investigative'],
  'burst-energy':   ['enterprising'],
  'social-energy':  ['social'],
  'solo-creator':   ['artistic'],
  'variety-seeker': ['enterprising', 'artistic'],
  'steady-builder': ['conventional', 'realistic'],
  'multitasker':    ['enterprising'],
};

// Cognitive-style scenarios. Selection format is "<scenario_id>:<A|B>".
// Each scenario’s A/B is a forced binary, so signal is high.
const COGNITIVE_MAP: Record<string, RiasecCode[]> = {
  // HS scenarios
  'cs-1:A': ['investigative'],                 // Analytical
  'cs-1:B': ['artistic'],                      // Creative
  'cs-2:A': ['investigative'],                 // Deep Learner
  'cs-2:B': ['social'],                        // Social Processor
  'cs-3:A': ['conventional', 'investigative'], // Systematic
  'cs-3:B': ['artistic', 'enterprising'],      // Intuitive
  'cs-4:A': ['conventional'],                  // Risk-Cautious
  'cs-4:B': ['enterprising'],                  // Risk-Seeking
};

// Skill / strength mappings. Same shape as above.
const SKILL_MAP: Record<string, RiasecCode[]> = {
  'analytical': ['investigative'],
  'creative': ['artistic'],
  'communication': ['social', 'enterprising'],
  'leadership': ['enterprising'],
  'tech-savvy': ['investigative', 'realistic'],
  'empathy': ['social'],
  'adaptability': ['social', 'enterprising'],
  'research': ['investigative'],
  'design': ['artistic', 'realistic'],
  'collaboration': ['social'],
  'organizing': ['conventional'],
  'hands-on': ['realistic'],
  'persuasion': ['enterprising'],
  'strategy': ['enterprising', 'investigative'],
  'execution': ['conventional', 'realistic'],
  'systems-thinking': ['investigative'],
  'influence': ['enterprising', 'social'],
  'sales': ['enterprising'],
  // Strength options from the strengths screen
  'pattern-recognition': ['investigative'],
  'storytelling': ['artistic', 'social'],
  'resilience': ['enterprising', 'realistic'],
  'curiosity': ['investigative'],
  'hustle': ['enterprising'],
  'vision': ['enterprising', 'artistic'],
  'calm-under-pressure': ['social', 'realistic'],
  'connector': ['social', 'enterprising'],
  'self-starter': ['enterprising'],
  'detail-oriented': ['conventional', 'investigative'],
};

export type RiasecScores = Record<RiasecCode, number>;

function emptyScores(): RiasecScores {
  return { realistic: 0, investigative: 0, artistic: 0, social: 0, enterprising: 0, conventional: 0 };
}

// Compute RIASEC scores from the user's selections so far.
// Weights chosen so each screen contributes meaningfully without any
// single screen dominating. Total signal roughly doubles vs. the
// pre-2026-05 version that only used interests/skills/strengths.
//
// Weights (per matching code per selection):
//   interests       = 5  (broad, multi-pick interest catalog)
//   aspirations     = 4  (forward-looking, 3-pick cap)
//   dream-wednesday = 5  (single forced choice; high signal per pick)
//   skills          = 3  (capped at 4 picks)
//   cognitive-style = 3  (forced binary A/B)
//   strengths       = 2  (5-pick cap)
//   values          = 2  (5-pick cap)
//   workstyle       = 2  (5-pick cap; only signal-rich options mapped)
export function scoreRiasec(selections: Record<string, string[]>): RiasecScores {
  const scores = emptyScores();

  for (const id of selections.interests || []) {
    for (const code of INTEREST_MAP[id] || []) scores[code] += 5;
  }
  for (const id of selections.aspirations || []) {
    for (const code of ASPIRATION_MAP[id] || []) scores[code] += 4;
  }
  for (const id of selections['dream-wednesday'] || []) {
    for (const code of VIBE_MAP[id] || []) scores[code] += 5;
  }
  for (const id of selections.skills || []) {
    for (const code of SKILL_MAP[id] || []) scores[code] += 3;
  }
  for (const id of selections['cognitive-style'] || []) {
    for (const code of COGNITIVE_MAP[id] || []) scores[code] += 3;
  }
  for (const id of selections.strengths || []) {
    for (const code of SKILL_MAP[id] || []) scores[code] += 2;
  }
  for (const id of selections.values || []) {
    for (const code of VALUE_MAP[id] || []) scores[code] += 2;
  }
  for (const id of selections['energy-workstyle'] || []) {
    for (const code of WORKSTYLE_MAP[id] || []) scores[code] += 2;
  }

  return scores;
}

// Returns the top N RIASEC codes by score (descending). Ties broken by
// canonical R-I-A-S-E-C order. If all scores are zero, returns an empty
// array so the UI can show its empty state.
export function topRiasec(scores: RiasecScores, n: number = 3): RiasecCode[] {
  const order: RiasecCode[] = ['realistic', 'investigative', 'artistic', 'social', 'enterprising', 'conventional'];
  const total = order.reduce((sum, c) => sum + scores[c], 0);
  if (total === 0) return [];
  return order
    .map(code => ({ code, score: scores[code] }))
    .sort((a, b) => b.score - a.score)
    .slice(0, n)
    .map(x => x.code);
}

// Convert RIASEC scores to a 0-100 percentage per code, useful for bar viz.
// Scaled against the highest single-code score in the input so the most
// dominant code reads 100%. Returns an empty record when there is no data.
export function riasecPercentages(scores: RiasecScores): Record<RiasecCode, number> {
  const max = Math.max(...Object.values(scores));
  const out = emptyScores();
  if (max === 0) return out;
  (Object.keys(scores) as RiasecCode[]).forEach(code => {
    out[code] = Math.round((scores[code] / max) * 100);
  });
  return out;
}

// Compact summary string. "Builder + Analyst + Helper" style. Useful for
// inline contexts where a full card is overkill.
export function riasecSummary(codes: RiasecCode[]): string {
  return codes.map(c => RIASEC_LABELS[c].plain).join(' + ');
}

// Letter-code summary. "RIA" style. The format counselors recognize.
export function riasecHollandCode(codes: RiasecCode[]): string {
  return codes.map(c => RIASEC_LABELS[c].letter).join('');
}
